/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit, inject, computed, effect } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { DropdownService } from '@portal/app/dashboard/home-page/services/dropdown.service';
import { TrendingChartService } from '@portal/app/dashboard/home-page/components/trending-chart/trending-chart.service';
import { FiscalCalendarStateService } from '@portal/app/shared/services/fiscal-calendar-state.service';
import { FiscalDatePresetService } from '@portal/app/shared/services/fiscal-date-preset.service';

@Component({
  selector: 'portal-trending-chart',
  templateUrl: './trending-chart.component.html'
})
export class TrendingChartComponent implements OnInit, OnDestroy {
  chartRelativeDay = '';
  private destroy$ = new Subject<void>();
  isLoading = true;
  relativeDayOptions?: SelectItem[];
  singleLineChartOptions: Highcharts.Options | undefined = undefined;
  isChartReady: boolean[] = [];
  previousRelativeDay: string | null = null;

  private fiscalCalendarStateService = inject(FiscalCalendarStateService);
  private fiscalDatePresetService = inject(FiscalDatePresetService);

  fiscalRelativeDayOptions = computed<SelectItem[]>(() => {
    return this.fiscalDatePresetService.datePresetOptions.map((option) => ({
      label: option.label,
      value: option.value
    }));
  });

  isFiscalCalendarEnabled = computed(() =>
    this.fiscalCalendarStateService.isCustomCalendarEnabled()
  );

  constructor(
    private dropdownService: DropdownService,
    private trendingChartService: TrendingChartService
  ) {
    effect(() => {
      const isFiscalEnabled = this.isFiscalCalendarEnabled();
      this.relativeDayOptions = isFiscalEnabled
        ? this.fiscalRelativeDayOptions()
        : this.dropdownService.getRelativeDayOptions();
    });
  }

  ngOnInit() {
    this.initializeRelativeDayOptions();
    this.subscribeToData();
  }

  private initializeRelativeDayOptions(): void {
    this.relativeDayOptions = this.isFiscalCalendarEnabled()
      ? this.fiscalRelativeDayOptions()
      : this.dropdownService.getRelativeDayOptions();
  }

  subscribeToData() {
    this.trendingChartService.chartData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((chartData) => {
        this.singleLineChartOptions = chartData;
      });

    this.trendingChartService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        if (isLoading !== this.isLoading) {
          this.isLoading = isLoading;
        }
      });

    this.trendingChartService.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        const relativeDay = this.trendingChartService.getRelativeDay(filters);
        if (relativeDay) {
          this.chartRelativeDay = relativeDay;
        }
      });
  }

  onChartRelativeDayChange(newValue: string) {
    if (newValue !== this.previousRelativeDay) {
      this.previousRelativeDay = newValue;
      this.trendingChartService.fetchDataWithTempFilters(newValue);
    }
  }

  // Handles cleanup when the service is destroyed
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.trendingChartService.resetLocalRelativeDay();
  }
}
